/**
 * Shared access helpers.
 */

/**
 * A "company-wide access" member — the audience for company-scope affordances
 * such as the department filter on the Goals and Leaderboard pages. True when
 * the user is a Company Admin, a Sub Admin, OR their work role grants any
 * workspace-wide view operation (`*.view_workspace`). The `*.view_workspace`
 * ops are the same company-scope signal the Members page keys off, so every
 * page that gates on "company-wide" now agrees on one definition instead of
 * each re-deriving it (Goals used a generic op match, Leaderboard a specific
 * one — they could drift).
 *
 * @param {{ role?: string, operations?: string[] } | null | undefined} user
 * @returns {boolean}
 */
export function isCompanyWideMember(user) {
  if (!user) return false
  if (user.role === 'company_admin' || user.role === 'sub_admin') return true
  return (user.operations ?? []).some((op) =>
    String(op).endsWith('.view_workspace'),
  )
}

/**
 * Whether the user may ASSIGN a goal to SOMEONE ELSE (pick an assignee other
 * than themselves when creating, or reassign later). Mirrors the backend
 * `canAssignToOthers` in goals.service.js: Company / Super / Sub Admins, or a
 * member whose Work Role can view a team's reports (Manager / Team Leader —
 * `reports.view_department` or `reports.view_workspace`). Everyone else owns
 * only their own goals. The server re-enforces this on every write, so this is
 * a UI gate only (which controls whether the Assignee picker is shown).
 *
 * @param {{ role?: string, operations?: string[] } | null | undefined} user
 * @returns {boolean}
 */
export function canAssignGoals(user) {
  if (!user) return false
  if (
    user.role === 'company_admin' ||
    user.role === 'super_admin' ||
    user.role === 'sub_admin'
  ) {
    return true
  }
  return (user.operations ?? []).some(
    (op) => op === 'reports.view_department' || op === 'reports.view_workspace',
  )
}
